import { format, startOfDay } from "date-fns";
import { AvailabilityResponse } from "../../../services/availability-and-rates/response/AvailabilityResponse";
import { DataCalendarResponse, BookingType } from "../../../services/availability-and-rates/response/DataCalendarResponse";
import { IndexRoomMaintenanceResponseType, RoomMaintenanceType } from "../../../services/room-maintenance/response/IndexRoomMaintenanceResponse";

interface MapBookingEventCalendarProps {
    booking: DataCalendarResponse | null;
    availability: AvailabilityResponse | null;
    unasigment: DataCalendarResponse | null;
    isUnsigmentChecked: boolean;
    showBaseOnRoomType: string;
    roomMaintenance: IndexRoomMaintenanceResponseType | null;
}

const mapBookingEventCalendar = ({
    booking,
    availability,
    unasigment,
    isUnsigmentChecked,
    showBaseOnRoomType,
    roomMaintenance
}: MapBookingEventCalendarProps) => {
    // Pastikan booking tidak null sebelum mapping.
    // Booking tanpa room_type_id (biasanya dari sync Channex/OTA yang room
    // type-nya belum dipetakan) tidak punya resource room type manapun untuk
    // ditempeli -- sebelumnya `resourceId` jadi '' dan booking-nya DIBUANG
    // DIAM-DIAM oleh FullCalendar. Sekarang dialihkan ke baris top-level
    // "Needs Attention" (`id: 'needs-attention'`, disuntik terpisah di
    // CalendarPage.tsx, bukan bagian dari MapRoomCalendar.ts).
    //
    // Drag SEKARANG DIIZINKAN untuk booking tanpa room_type_id juga (dulu
    // dimatikan lewat `resourceEditable: false`). Alasan diizinkan: pindahkan
    // booking dari baris "Needs Attention" ke kamar sungguhan lewat drag &
    // drop tetap memicu modal Move Reservation (lihat CalendarBooking.tsx
    // `eventDrop` -> `handleOpenMoveEventModal`), dan modal itu sudah
    // disesuaikan (MoveReservationContent.tsx, mode `isUnmapped`) untuk
    // mewajibkan user memilih rate plan baru sebelum submit ketika booking
    // asalnya belum punya rate_plan_id -- jadi drag-drop saja sudah cukup
    // untuk mulai proses resolusi, tidak perlu dipaksa lewat tombol "Fix it".
    const bookingEvent = (booking?.data ?? []).map((booking: BookingType) => ({
        id: booking.id,
        resourceId: !booking.room_type_id ? 'needs-attention' : (booking.room_id ?? ''),
        title: `${booking.customer_name} ${booking.customer_surname}`,
        start: format(startOfDay(new Date(booking.checkin_date)), "yyyy-MM-dd'T'HH:mm:ss"),
        end: format(startOfDay(new Date(booking.checkout_date)), "yyyy-MM-dd'T'HH:mm:ss"),
        extendedProps: {
            ...booking,
            type: 'booking'
        }
    }));

    // Pastikan availability tidak null sebelum mapping
    const availabilityEvent = availability?.data
        ? Object.entries(availability.data).flatMap(([uuid, dates]) =>
            Object.entries(dates).map(([date, value]) => ({
                id: `${uuid}-${date}`,
                resourceId: uuid ?? '',
                title: value.toString(),
                start: format(startOfDay(new Date(date)), "yyyy-MM-dd'T'HH:mm:ss"),
                end: format(startOfDay(new Date(date)), "yyyy-MM-dd'T'HH:mm:ss"),
                extendedProps: {
                    type: 'availability'
                }
            }))
        )
        : [];

    // Pastikan unasigment tidak null sebelum mapping. Booking tanpa
    // room_type_id sudah ditangani oleh `bookingEvent` di atas (resourceId
    // 'needs-attention') -- di-skip di sini supaya tidak diproses dua kali
    // (baris "Unassigned" per room type memang butuh room_type_id valid).
    const unassignedBookingEvent = (isUnsigmentChecked || showBaseOnRoomType !== '') && unasigment?.data
        ? unasigment.data.filter((booking: BookingType) => booking.room_type_id).map((booking: BookingType) => ({
            id: `${booking.id}`,
            resourceId: `unsigment-${booking.room_type_id}`,
            title: `${booking.customer_name} ${booking.customer_surname}`,
            start: format(startOfDay(new Date(booking.checkin_date)), "yyyy-MM-dd'T'HH:mm:ss"),
            end: format(startOfDay(new Date(booking.checkout_date)), "yyyy-MM-dd'T'HH:mm:ss"),
            extendedProps: {
                ...booking,
                type: 'unasigment'
            }
        }))
        : [];

    // Pastikan roomMaintenance tidak null sebelum mapping
    const roomMaintenanceEvent = (roomMaintenance?.data ?? []).map((roomMaintenance: RoomMaintenanceType) => ({
        id: roomMaintenance.id,
        resourceId: roomMaintenance.room_id ?? '',
        title: 'Room Maintenance',
        start: format(startOfDay(new Date(roomMaintenance.checkin_date)), "yyyy-MM-dd'T'HH:mm:ss"),
        end: format(startOfDay(new Date(roomMaintenance.checkout_date)), "yyyy-MM-dd'T'HH:mm:ss"),
        extendedProps: {
            ...roomMaintenance,
            type: 'roomMaintenance'
        }
    }));

    return [
        ...bookingEvent,
        ...availabilityEvent,
        ...unassignedBookingEvent,
        ...roomMaintenanceEvent
    ];
};

export default mapBookingEventCalendar;